import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import { getRoleFamilies, resolveVisibleRoleFamilyLabel } from "@/lib/intake-data";
import { Button } from "@/components/ui/button";
import { ArrowRight, Lock } from "lucide-react";

/**
 * v5.1 P1.1 — single-question re-intake gate.
 *
 * Triggered when the backend reports `requires_re_intake=true` (legacy
 * `leadership` users). Vendor type, first name, and seniority tier are
 * pre-filled and read-only — only `role_family` is asked. Posts back to
 * `/intake/stage1` (which clears the re-intake flag) and bounces to
 * `/intelligence`.
 */
export default function ReIntakePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [intake, setIntake] = useState(null);
  const [roleFamily, setRoleFamily] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user === null) {
      navigate("/signin", { replace: true });
      return;
    }
    if (user === undefined) return;

    (async () => {
      try {
        const { data } = await API.get("/intake/responses");
        if (!data?.requires_re_intake) {
          // No re-intake flag — bounce back to reveal.
          navigate("/intelligence", { replace: true });
          return;
        }
        setIntake(data);
      } catch (err) {
        setError(formatApiError(err));
      }
    })();
  }, [user, navigate]);

  const handleSubmit = async () => {
    if (!roleFamily || !intake) return;
    setLoading(true);
    setError("");
    try {
      await API.post("/intake/stage1", {
        first_name: user?.first_name || intake?.first_name || "",
        company_type: intake.company_type,
        role_family: roleFamily,
      });
      navigate("/intelligence", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  if (!intake) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <p className="text-sm text-zinc-600" data-testid="re-intake-loading">Loading…</p>
      </div>
    );
  }

  const roleOptions = getRoleFamilies(intake.company_type);
  const legacyLabel = resolveVisibleRoleFamilyLabel(intake.legacy_role_family || intake.role_family || "");

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-2xl mx-auto px-4 py-12" data-testid="re-intake-page">
        <div className="bg-white border border-zinc-200 p-8">
          <p
            className="uppercase mb-2 text-amber-800"
            style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em" }}
            data-testid="re-intake-eyebrow"
          >
            One quick refresh
          </p>
          <h1 className="font-heading mb-3" style={{ fontSize: "28px", fontWeight: 600, color: "#111111" }} data-testid="re-intake-heading">
            Your role family is being refined.
          </h1>
          <p className="text-[15px] text-zinc-700 leading-relaxed mb-6" data-testid="re-intake-subhead">
            We've simplified the role-family options to four. Your prior selection
            (<span className="text-zinc-900 font-medium">{legacyLabel}</span>) was a
            posture rather than a sales motion, so we're asking you to pick the
            current motion that best matches what you do day-to-day. Vendor type
            and seniority stay as you set them.
          </p>

          <div className="space-y-4 mb-6 text-[13px]">
            <div className="flex items-center gap-2 text-zinc-600">
              <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>
                Vendor type: <span className="text-zinc-900 font-medium">{intake.company_type}</span> · Seniority:{" "}
                <span className="text-zinc-900 font-medium">{intake.seniority_tier}</span>
              </span>
            </div>
          </div>

          <p
            className="uppercase mb-3 text-amber-800"
            style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em" }}
            data-testid="re-intake-question-label"
          >
            What you are trying to move
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6" role="radiogroup" aria-label="Role family">
            {roleOptions.map((opt) => {
              const selected = roleFamily === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setRoleFamily(opt.value)}
                  className={`text-left px-4 py-3 border transition-colors ${
                    selected
                      ? "border-amber-700 bg-amber-50 text-zinc-900"
                      : "border-zinc-200 bg-white text-zinc-900 hover:border-zinc-400"
                  }`}
                  data-testid={`re-intake-role-${opt.value}`}
                >
                  <span className="text-[14px] font-medium">{opt.label}</span>
                </button>
              );
            })}
          </div>

          {error && (
            <p className="text-sm text-red-600 mb-3" data-testid="re-intake-error">
              {error}
            </p>
          )}

          <Button
            onClick={handleSubmit}
            disabled={!roleFamily || loading}
            className="rounded-none bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="re-intake-submit"
          >
            {loading ? "Saving…" : "Continue to my Reveal"}
            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
          </Button>
        </div>
      </div>
    </div>
  );
}
